import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import {
  saveSendTimeRestrictions as saveSendTimeRestrictionsApi,
  updateMessageOptions as updateMessageOptionsApi,
  getCustomerTemplates,
  uploadAriResponses as uploadAriResponsesApi,
  importAriResponses as importAriResponsesApi,
  sequences as sequencesApi,
  customerBasic as customerBasicApi,
  fetchAriIntents as fetchAriIntentsApi,
  fetchVectorSearchResults as fetchVectorSearchResultsApi,
  integrationEvents as integrationEventsApi,
  fetchAriIntentData as fetchAriIntentDataApi,
  addAriIntentData as addAriIntentDataApi,
  upgradeSequenceToUseKnowledgeBaseApi,
  downloadAriResponsesApi,
  saveIntegrationFieldsApi,
  getTermsOfService,
  agreeTermsOfService,
  getOrCreateDemoJobchatSequence as getOrCreateDemoJobchatSequenceApi,
  getCorpusData,
  removeCorpusData,
  downloadCorpusData as downloadCorpusDataApi,
  updateSequenceDefinition as updateSequenceDefinitionApi,
  uploadCorpusData as uploadCorpusDataApi,
  importStructuredResponses as importStructuredResponsesApi,
} from 'util/Api/customer';
import { fetchUsersOmni as fetchUsersOmniApi } from 'util/Api/user';
import { saveMessageTemplate, removeIntegrationMessageTemplate } from 'util/Api/campaign';
import { channelTypes, productTypes } from 'appConstants';

export const resetAriImportModalSuccessFlag = createAction(
  'customer/resetAriImportModalSuccessFlag',
);

export const resetIntegrationSettingsStatus = createAction(
  'customer/resetIntegrationSettingsStatus',
);
export const resetAddAriImportModalSuccessFlag = createAction(
  'customer/resetAddAriImportModalSuccessFlag',
);

export const resetUploadCorpusDataStatus = createAction('customer/resetUploadCorpusDataStatus');

export const resetSearchedUsers = createAction('campaigns/resetSearchedUsers');

export const setPreferenceDirty = createAction('customer/setPreferenceDirty');

export const saveSendTimeRestrictions = createAsyncThunk(
  'customer/send_time_restriction/update',
  async ({ enabled, startHourLocal, endHourLocal, localTimezone }, { rejectWithValue }) => {
    try {
      return await saveSendTimeRestrictionsApi({
        enabled,
        startHourLocal,
        endHourLocal,
        localTimezone,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateMessageOptions = createAsyncThunk(
  'customer/message_options/update',
  async (
    {
      templateStrictMode,
      disableFreetext = false,
      customizedOptOutMessageText,
      initialOptoutEnabled,
      userCanManageOptOut,
    },
    { rejectWithValue },
  ) => {
    try {
      return await updateMessageOptionsApi({
        templateStrictMode,
        disableFreetext,
        customizedOptOutMessageText,
        initialOptoutEnabled,
        userCanManageOptOut,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const getCustomerTemplatesApi = async (
  {
    pageNumber,
    itemsPerPage,
    sort,
    code = null,
    shareFilter = 'SHAREONLY',
    isWhatsApp = false,
    isSharedTemplate = false,
  },
  { rejectWithValue },
) => {
  if (isWhatsApp) {
    code = channelTypes.WHATS_APP.toLowerCase();
  } else if (isSharedTemplate) {
    code = productTypes.TEXT_RECRUIT.toLowerCase();
  }

  try {
    return await getCustomerTemplates({
      pageNumber,
      itemsPerPage,
      sort,
      code,
      shareFilter,
    });
  } catch (err) {
    return rejectWithValue(err);
  }
};

export const fetchCustomerTemplates = createAsyncThunk(
  'customer/fetchCustomerTemplates',
  getCustomerTemplatesApi,
);

export const fetchWhatsAppTemplates = createAsyncThunk(
  'customer/fetchWhatsAppTemplates',
  getCustomerTemplatesApi,
);

export const saveCustomerTemplate = createAsyncThunk(
  'campaigns/saveCustomerTemplate',
  async ({ code, name, text, templateId, shareTemplate }, { rejectWithValue }) => {
    try {
      return await saveMessageTemplate({
        code,
        name,
        text,
        templateId,
        shareTemplate,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const deleteCustomerTemplate = createAsyncThunk(
  'campaigns/deleteCustomerTemplate',
  async (templateId, { rejectWithValue }) => {
    try {
      return await removeIntegrationMessageTemplate(templateId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const resetTemplateSaveStatus = createAction('thread/resetTemplateSaveStatus');
export const setIsEdittrue = createAction('thread/setIsEdittrue');
export const setTemplateTypeIsCustomer = createAction('thread/setTemplateTypeIsCustomer');
export const setTemplateTypeIsCustomerFalse = createAction('thread/setTemplateTypeIsCustomerFalse');
export const resetBasicDetailsStatus = createAction('customer/resetBasicDetailsStatus');

export const uploadAriResponses = createAsyncThunk(
  'customer/uploadAriResponses',
  async ({ selectedFile, uid }, { rejectWithValue }) => {
    try {
      return await uploadAriResponsesApi({ selectedFile, uid });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const importAriResponses = createAsyncThunk(
  'customer/importAriResponses',
  async (
    { uploadHeaders, customerId, campaignId, uid, selectedAriSequence, ari },
    { rejectWithValue },
  ) => {
    try {
      return await importAriResponsesApi({
        uploadHeaders,
        customerId,
        campaignId,
        uid,
        selectedAriSequence,
        ari,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const sequences = createAsyncThunk(
  'customer/sequences',
  async ({ filter, hasConfigurableAri, hasRecruitingAri }, { rejectWithValue }) => {
    try {
      return await sequencesApi({ filter, hasConfigurableAri, hasRecruitingAri });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchBasicDetails = createAsyncThunk(
  'customer/basicDetails',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { customerId } = getState().user;
      return await customerBasicApi({ accountId: customerId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchAriIntents = createAsyncThunk(
  'customer/fetchAriIntents',
  async (
    { sequenceId, page, itemsPerPage, sort, ariIntentsSearchKey, language, ariType },
    { rejectWithValue },
  ) => {
    try {
      return await fetchAriIntentsApi({
        sequenceId,
        page,
        itemsPerPage,
        sort,
        language,
        ariType,
        ariIntentsSearchKey,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchVectorSearchResults = createAsyncThunk(
  'customer/fetchVectorSearchResults',
  async ({ searchAriQuery, sequenceId }, { rejectWithValue }) => {
    try {
      return await fetchVectorSearchResultsApi({ searchAriQuery, sequenceId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const integrationEvents = createAsyncThunk(
  'customer/integrationEvents',
  async ({ filter, itemsPerPage, sort, code, pageNumber }, { rejectWithValue }) => {
    try {
      return await integrationEventsApi({
        filter,
        itemsPerPage,
        sort,
        code,
        pageNumber,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
export const fetchAriIntentData = createAsyncThunk(
  'customer/fetchAriIntentData',
  async ({ sequenceId, ariCategory, accountId, ari }, { rejectWithValue }) => {
    try {
      return await fetchAriIntentDataApi({
        sequenceId,
        accountId,
        ariCategory,
        ari,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const addAriIntentData = createAsyncThunk(
  'customer/addAriIntentData',
  async ({ addIntent, name, escalate, sequenceId, ari }, { rejectWithValue }) => {
    try {
      return await addAriIntentDataApi({
        addIntent,
        name,
        escalate,
        sequenceId,
        ari,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const downloadAriResponses = createAsyncThunk(
  'customer/downloadAriResponses',
  async ({ sequenceId, isLegacyAri }, { rejectWithValue }) => {
    try {
      return await downloadAriResponsesApi({ sequenceId, isLegacyAri });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchTermsOfService = createAsyncThunk(
  'customer/fetchTermsOfService',
  async ({ termsOfServiceRecordId }, { rejectWithValue }) => {
    try {
      return await getTermsOfService({ termsOfServiceRecordId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const confirmTermsOfService = createAsyncThunk(
  'customer/confirmTermsOfService',
  async ({ termsOfServiceRecord }, { rejectWithValue }) => {
    try {
      return await agreeTermsOfService({ termsOfServiceRecord });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const saveBasicDetails = createAsyncThunk(
  'customer/saveBasicDetails',
  async ({ fields, customerId }, { rejectWithValue }) => {
    try {
      return await saveIntegrationFieldsApi({ fields, customerId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchUsersOmni = createAsyncThunk(
  'campaigns/fetchUsersOmni',
  async ({ params }, { rejectWithValue }) => {
    params = {
      isAutoSuggest: params.isAutoSuggest,
      itemsPerPage: params.itemsPerPage,
      page: params.page,
      search: params.search,
    };
    try {
      return await fetchUsersOmniApi({ params });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getOrCreateDemoJobchatSequence = createAsyncThunk(
  'customer/getOrCreateDemoJobchatSequence',
  async ({ sequenceDefinitionId }, { rejectWithValue }) => {
    try {
      return await getOrCreateDemoJobchatSequenceApi({ sequenceDefinitionId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchCorpusData = createAsyncThunk(
  'customer/fetchCorpusData',
  async ({ ariType, sequenceId }, { rejectWithValue }) => {
    try {
      return await getCorpusData({ ariType, sequenceId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const deleteCorpusData = createAsyncThunk(
  'customer/deleteCorpusData',
  async ({ ariType, fileName, sequenceId }, { rejectWithValue }) => {
    try {
      return await removeCorpusData({ ariType, fileName, sequenceId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const upgradeSequenceToUseKnowledgeBase = createAsyncThunk(
  'customer/upgradeSequenceToUseKnowledgeBase',
  async ({ sequenceId }, { rejectWithValue }) => {
    try {
      return await upgradeSequenceToUseKnowledgeBaseApi({ sequenceId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const downloadCorpusData = createAsyncThunk(
  'customer/downloadCorpusData',
  async ({ fileKey }, { rejectWithValue }) => {
    try {
      return await downloadCorpusDataApi({ fileKey });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const uploadCorpusData = createAsyncThunk(
  'customer/uploadCorpusData',
  async ({ ariType, files, sequenceId, replaceFileName }, { rejectWithValue }) => {
    try {
      return await uploadCorpusDataApi({ ariType, files, sequenceId, replaceFileName });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const importStructuredResponses = createAsyncThunk(
  'customer/importStructuredResponses',
  async ({ sequenceId }, { rejectWithValue }) => {
    try {
      return await importStructuredResponsesApi({ sequenceId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateSequenceDefinition = createAsyncThunk(
  'customer/updateSequenceDefinition',
  async (
    {
      sequenceId,
      useDefaultGreeting,
      useDefaultAffirmativeResponse,
      digitalAssistantGreeting,
      greetingAffirmativeResponse,
    },
    { rejectWithValue },
  ) => {
    try {
      return await updateSequenceDefinitionApi({
        sequenceId,
        useDefaultGreeting,
        useDefaultAffirmativeResponse,
        digitalAssistantGreeting,
        greetingAffirmativeResponse,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
